import type { VendorOption } from './checks';
import { reviewRequestError } from '../utils/reviewErrors';

export type NewItemReviewStatus =
  | 'pending' | 'auto_approved' | 'approved' | 'skipped' | 'postponed' | 'created' | 'create_failed'
  | 'auto_skipped';
export type NewItemReviewAction = 'approved' | 'postponed' | 'skipped';

/** Human-readable NetSuite import payload — mirrors the legacy CHECK_* CSV
 *  columns (snake_cased). Values are raw (booleans/numbers, no rendering);
 *  null = not part of the staged create payload. */
export interface NewItemImportPayload {
  item_name_number: string | null;
  vendor_number_upc: string | null;
  display_name_code: string | null;
  cost_source: string | null;
  item_price_category: string | null;
  purchase_cost: number | null;
  status: string | null;
  return_status: string | null;        // "Non-Returnable Part" | null (null = returnable)
  core_charge: boolean;
  core_charge_amount: number | null;
  purchase_description: string | null;
  manufacturer: string | null;
  country_of_manufacture: string | null;
  vendor1_name: string | null;
  vendor1_preferred: boolean | null;
  vendor1_purchase_price: number | null;
  sales_description: string | null;
  item_weight: number | null;
  height_inch: number | null;
  width_inch: number | null;
  length_inch: number | null;
  warranty: string | null;
  product_type: string | null;
  manufacturer_country: string | null;
  made_in_usa: boolean | null;
  home_value: string | null;
  url: string | null;
  debug_time_stamp: string | null;     // the scrape timestamp (= scraped_at)
  item_name_in_ns: boolean;
  upc_code_in_ns: boolean;
  already_in_netsuite: boolean;
  sku_source: string | null;
}

export interface NewItem {
  id: number;
  vendor_code: string;
  /** Client-facing vendor label — tbvendors.name, falling back to the code. */
  vendor_name: string;
  sku: string;
  item_name: string | null;
  display_name: string | null;
  vendor_price: number | null;
  manufacturer: string | null;
  status_label: string | null;
  ns_record: Record<string, unknown>;
  review_status: NewItemReviewStatus;
  reviewed_at: string | null;
  reviewer_email: string | null;
  screenshot_url: string | null;
  external_id: string | null;
  ns_product_id: number | null;
  ns_created_at: string | null;
  ns_create_error: string | null;
  create_attempts: number | null;
  /** Vendor availability — null until the vendor's mapper supplies it. */
  vendor_stock: number | null;
  /** When the scraper read the data (tbpartsdata.checked_at). */
  scraped_at: string | null;
  /** Which tier of the dims resolution cascade set weight/dims (BOT-352) —
   *  "joes_rules" | "website" | "ai" | "price_range", or null for vendors
   *  whose mapper doesn't supply it. */
  dims_source: string | null;
  /** Extra context for dims_source — the matched Joe's rule name, or the
   *  AI tier's confidence + reason. Null when there's none. */
  dims_source_detail: string | null;
  created_at: string | null;
  updated_at: string | null;
  /** The legacy CHECK_*-shaped view of ns_record — drives the expandable
   *  detail panel and the new queue columns. */
  import_payload: NewItemImportPayload;
}

export interface NewItemsPage {
  items: NewItem[];
  total: number;
  page: number;
  per_page: number;
}

export async function fetchNewItemVendors(): Promise<VendorOption[]> {
  const res = await fetch('/api/v1/new-items/vendor-codes', { credentials: 'include' });
  if (!res.ok) throw new Error(`Vendors fetch failed: ${res.status}`);
  const data = await res.json() as { vendors: VendorOption[] };
  return data.vendors;
}

export async function fetchNewItemsQueue(
  filters: { vendor_codes?: string[]; page?: number; per_page?: number } = {},
): Promise<NewItemsPage> {
  const params = new URLSearchParams();
  if (filters.vendor_codes?.length) {
    filters.vendor_codes.forEach(vc => params.append('vendor_code', vc));
  }
  if (filters.page)        params.set('page', String(filters.page));
  if (filters.per_page)    params.set('per_page', String(filters.per_page));

  const res = await fetch(`/api/v1/new-items/queue?${params}`, { credentials: 'include' });
  if (!res.ok) throw new Error(`New items queue fetch failed: ${res.status}`);
  return res.json() as Promise<NewItemsPage>;
}

export async function fetchNewItemsProcessed(
  filters: { vendor_codes?: string[]; review_status?: NewItemReviewStatus; page?: number; per_page?: number } = {},
): Promise<NewItemsPage> {
  const params = new URLSearchParams();
  if (filters.vendor_codes?.length) {
    filters.vendor_codes.forEach(vc => params.append('vendor_code', vc));
  }
  if (filters.review_status) params.set('review_status', filters.review_status);
  if (filters.page)          params.set('page', String(filters.page));
  if (filters.per_page)      params.set('per_page', String(filters.per_page));

  const res = await fetch(`/api/v1/new-items/processed?${params}`, { credentials: 'include' });
  if (!res.ok) throw new Error(`New items processed fetch failed: ${res.status}`);
  return res.json() as Promise<NewItemsPage>;
}

export async function reviewNewItem(id: number, action: NewItemReviewAction): Promise<void> {
  const res = await fetch(`/api/v1/new-items/${id}/review`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action }),
  });
  if (!res.ok) throw await reviewRequestError(res, 'Review');
}

export async function bulkReviewNewItems(ids: number[], action: NewItemReviewAction): Promise<number> {
  const res = await fetch('/api/v1/new-items/bulk-review', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids, action }),
  });
  if (!res.ok) throw await reviewRequestError(res, 'Bulk review');
  const data = await res.json() as { updated: number };
  return data.updated;
}

export type DimsSourceType = 'joes_rules' | 'website' | 'ai' | 'price_range' | 'manual';

export interface DimsSourceOption {
  dims_source: DimsSourceType;
}

export interface CostSourceOption {
  id: string;
  label: string;
}

export async function fetchNewItemsReview(filters: {
  vendor_codes?: string[];
  dims_sources?: string[];
  search?: string;
  price_min?: number;
  price_max?: number;
  manufacturers?: string[];
  status_labels?: string[];
  cost_sources?: string[];
  return_filter?: string;
  core_filter?: string;
  stock_min?: number;
  stock_max?: number;
  page?: number;
  per_page?: number;
}): Promise<NewItemsPage> {
  const params = new URLSearchParams();
  filters.vendor_codes?.forEach(v => params.append('vendor_code', v));
  filters.dims_sources?.forEach(d => params.append('dims_source', d));
  filters.manufacturers?.forEach(m => params.append('manufacturer', m));
  filters.status_labels?.forEach(s => params.append('status_label', s));
  filters.cost_sources?.forEach(c => params.append('cost_source', c));
  if (filters.search) params.set('search', filters.search);
  if (filters.price_min != null) params.set('price_min', String(filters.price_min));
  if (filters.price_max != null) params.set('price_max', String(filters.price_max));
  if (filters.return_filter) params.set('return_filter', filters.return_filter);
  if (filters.core_filter) params.set('core_filter', filters.core_filter);
  if (filters.stock_min != null) params.set('stock_min', String(filters.stock_min));
  if (filters.stock_max != null) params.set('stock_max', String(filters.stock_max));
  if (filters.page) params.set('page', String(filters.page));
  if (filters.per_page) params.set('per_page', String(filters.per_page));
  const res = await fetch(`/api/v1/new-items/review?${params}`, { credentials: 'include' });
  if (!res.ok) throw new Error(`New items review fetch failed: ${res.status}`);
  return res.json() as Promise<NewItemsPage>;
}

export async function fetchDimsSources(): Promise<DimsSourceOption[]> {
  const res = await fetch('/api/v1/new-items/review/dims-sources', { credentials: 'include' });
  if (!res.ok) throw new Error(`Dims sources fetch failed: ${res.status}`);
  const data = await res.json() as { sources: DimsSourceOption[] };
  return data.sources;
}

export async function fetchReviewManufacturers(): Promise<string[]> {
  const res = await fetch('/api/v1/new-items/review/manufacturers', { credentials: 'include' });
  if (!res.ok) throw new Error(`Manufacturers fetch failed: ${res.status}`);
  const data = await res.json() as { manufacturers: string[] };
  return data.manufacturers;
}

export async function fetchReviewStatusLabels(): Promise<string[]> {
  const res = await fetch('/api/v1/new-items/review/status-labels', { credentials: 'include' });
  if (!res.ok) throw new Error(`Status labels fetch failed: ${res.status}`);
  const data = await res.json() as { status_labels: string[] };
  return data.status_labels;
}

export async function fetchReviewCostSources(): Promise<CostSourceOption[]> {
  const res = await fetch('/api/v1/new-items/review/cost-sources', { credentials: 'include' });
  if (!res.ok) throw new Error(`Cost sources fetch failed: ${res.status}`);
  const data = await res.json() as { cost_sources: CostSourceOption[] };
  return data.cost_sources;
}

export async function updateNewItemDims(
  id: number,
  dims: { height: number; width: number; length: number; weight: number },
): Promise<void> {
  const res = await fetch(`/api/v1/new-items/${id}/dims`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(dims),
  });
  if (!res.ok) throw new Error(`Update dims failed: ${res.status}`);
}

export async function bulkUpdateNewItemDims(
  ids: number[],
  dims: { height: number; width: number; length: number; weight: number },
): Promise<number> {
  const res = await fetch('/api/v1/new-items/bulk-dims', {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids, ...dims }),
  });
  if (!res.ok) throw new Error(`Bulk update dims failed: ${res.status}`);
  const data = await res.json() as { updated: number };
  return data.updated;
}

export async function undoNewItem(id: number): Promise<void> {
  const res = await fetch(`/api/v1/new-items/queue/${id}/undo`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) throw new Error(`Undo failed: ${res.status}`);
}
